import { useMutation } from '@apollo/client';

import type {
  ArticlesError,
  ContentError,
} from '@xing-com/crate-common-graphql-types';
import { ContentArticleStatus } from '@xing-com/crate-common-graphql-types';

import { EntityPagesArticleModuleArticlesArticleEnableDocument } from '../graphql/mutations/entity-pages-article-module-articles-article-enable.gql-types';
import { EntityPagesArticleModuleContentArticleEnableDocument } from '../graphql/mutations/entity-pages-article-module-content-article-enable.gql-types';
import type { ArticleCardView } from '../types/types';

type EnableArticleParams = {
  article: ArticleCardView;
};

export const useEnableArticleMutation = (options?: {
  onCompleted?: (article: ArticleCardView) => void;
  onError?: (error: ContentError | ArticlesError | Error) => void;
}) => {
  const [handleArticlesArticleEnable, contentArticleState] = useMutation(
    EntityPagesArticleModuleArticlesArticleEnableDocument
  );
  const [handleContentArticleEnable, articlesArticleState] = useMutation(
    EntityPagesArticleModuleContentArticleEnableDocument
  );
  const setArticle = (article: ArticleCardView) => ({
    ...article,
    publicationState: ContentArticleStatus.Published,
  });

  const handleEnableArticle = ({ article }: EnableArticleParams) => {
    if (article.typename === 'ContentArticle') {
      handleContentArticleEnable({
        variables: {
          input: { globalId: article.globalId },
        },
        onCompleted: (data) => {
          const error = data.contentEnableArticle?.error;
          if (error) {
            options?.onError?.(error);
          } else {
            options?.onCompleted?.(setArticle(article));
          }
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      });
    } else {
      handleArticlesArticleEnable({
        variables: {
          input: { articleGlobalId: article.globalId },
        },
        onCompleted: (data) => {
          const error =
            data.articlesEnableArticle?.__typename === 'ArticlesError'
              ? data.articlesEnableArticle
              : null;
          if (error) {
            options?.onError?.(error);
          } else {
            options?.onCompleted?.(setArticle(article));
          }
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      });
    }
  };

  return {
    handleEnableArticle,
    loading: contentArticleState.loading || articlesArticleState.loading,
  };
};
